import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "@mindlercare/ui-web";
import {
  SelectIcbt,
  IcbtTranslationProvider,
  ProgramPages,
  type Language as IcbtLanguage,
} from "@mindlercare/icbt";
import { Questionnaire } from "components/icbt/programs/ContentfulRichText/Questionnaire";
import { useIcbtTranslations } from "../../hooks/useIcbtTranslations";
import { useFlag } from "@mindlercare/unleash-client";
import { IcbtClaims } from "api/types";
import { isForbiddenError } from "api/helpers";
import { useAssignIcbt } from "../assignIcbtContext";
import * as R from "remeda";
import { Typography } from "components/typography";
import { PreviewProgramModal } from "components/patients/patientCard/icbt/modals/PreviewProgramModal";
import { useGetProgramContentQuery } from "../../../../hooks/icbt/useGetProgramContentQuery";
import { MappedTreatmentData, PossiblyErrors } from "../common";
import { CenteredContainer } from "./icbtSelector.styles";
import { SelectFeedbackDates } from "./selectFeedbackDates";
import { Language } from "core/types/index";

interface IcbtSelectorProps {
  progressStep: number;
  setProgressStep: Dispatch<SetStateAction<number>>;
  isLoading: boolean;
  isPreviewOnly: boolean;
  closeAssignIcbtModal: () => void;
  icbtClaims?: IcbtClaims;
  handleOpenConfirm?: () => void;
  handleSelectTreatment?: (treatmentId?: string) => void;
  treatments: MappedTreatmentData;
  assigningEnabled: boolean;
  isError: boolean;
  possiblyErrors: PossiblyErrors;
  feedbackDateFeatureEnabledForPatient: boolean;
  setSelectedLanguage: Dispatch<SetStateAction<Language>>;
  selectedLanguage: Language;
  languages?: IcbtLanguage[];
  patientUserId: string;
}

export const IcbtSelector = ({
  progressStep,
  setProgressStep,
  isLoading,
  isPreviewOnly,
  closeAssignIcbtModal,
  icbtClaims,
  handleOpenConfirm,
  handleSelectTreatment,
  treatments,
  assigningEnabled,
  isError,
  possiblyErrors,
  feedbackDateFeatureEnabledForPatient,
  setSelectedLanguage,
  selectedLanguage,
  languages,
  patientUserId,
}: IcbtSelectorProps) => {
  const { t } = useTranslation();
  const enableAudio = useFlag("APP_ICBT_SOUND");
  const enableVideo = useFlag("APP_ICBT_VIDEO");
  const { state: assignIcbtState, dispatch } = useAssignIcbt();
  const assignState = assignIcbtState.assignState;
  const selectedTreatmentIdFromRedux = assignIcbtState.selectedTreatmentId;

  const [previewProgramId, setPreviewProgramId] = useState<string>();
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const introTreatmentId = useMemo(
    () => treatments.sorted.find((tr) => tr.introduction)?.id,
    [treatments],
  );
  const initialSelectedTreatmentId =
    selectedTreatmentIdFromRedux ?? introTreatmentId;

  // Preselect first program of initial treatment for preview
  const initialPreviewProgramId = useMemo(() => {
    if (!initialSelectedTreatmentId) return undefined;
    return treatments.byId[initialSelectedTreatmentId]?.programs[0]?.id;
  }, [initialSelectedTreatmentId, treatments]);

  // Use initialPreviewProgramId as default, then user clicks override it
  const effectivePreviewProgramId = previewProgramId ?? initialPreviewProgramId;

  const translations = useIcbtTranslations();

  const selectIcbtAssignButtonHandler = useCallback(() => {
    if (feedbackDateFeatureEnabledForPatient) {
      setProgressStep(1);
    } else {
      handleOpenConfirm?.();
    }
  }, [
    setProgressStep,
    handleOpenConfirm,
    feedbackDateFeatureEnabledForPatient,
  ]);

  const programsToAssign = assignState?.programsToAssign;

  const programsToBeAssignedPairedWithTreatment = R.pipe(
    programsToAssign ?? {},
    R.toPairs,
    R.map(([treatmentId, programIds]) =>
      R.map(programIds, (programId) => [treatmentId, programId]),
    ),
    R.flatten(),
  );

  const programsToBeAssigned = programsToBeAssignedPairedWithTreatment.map(
    ([_, programId]) => programId,
  );

  const handleProgramSelectionChanged = (
    programId: string,
    treatmentId?: string,
  ) => {
    dispatch({
      type: "setAssignState",
      payload: { programIds: [programId], treatmentId },
    });
  };

  const handleSelectProgram = (programIds: string[], treatmentId?: string) => {
    dispatch({ type: "setAssignState", payload: { programIds, treatmentId } });
  };

  const {
    data: programContent,
    isFetching: programContentFetching,
    error: programContentError,
  } = useGetProgramContentQuery({
    programId: effectivePreviewProgramId,
    languageCode: selectedLanguage?.codeISO2,
  });

  switch (progressStep) {
    case 0: {
      if (isError) {
        return (
          <CenteredContainer>
            <Typography insensitive variant="h3">
              {Object.values(possiblyErrors).some(isForbiddenError)
                ? t("ICBT_LOADING_DATA_ERROR_FORBIDDEN")
                : t("ICBT_LOADING_DATA_ERROR")}
            </Typography>
          </CenteredContainer>
        );
      }
      if (isLoading) {
        return (
          <CenteredContainer>
            <Spinner />
          </CenteredContainer>
        );
      }
      return (
        <IcbtTranslationProvider translations={translations}>
          <SelectIcbt
            treatments={treatments}
            selectedTreatmentId={initialSelectedTreatmentId}
            selectedProgramId={effectivePreviewProgramId}
            programsToAssign={programsToAssign}
            assigningEnabled={assigningEnabled}
            isPreviewOnly={isPreviewOnly}
            closeAssignIcbtModal={closeAssignIcbtModal}
            selectIcbtAssignButtonHandler={selectIcbtAssignButtonHandler}
            handleSelectTreatment={(treatmentId) => {
              handleSelectTreatment?.(treatmentId);
              // Preselect first program for preview
              if (treatmentId) {
                const firstProgram = treatments.byId[treatmentId]?.programs[0];
                if (firstProgram) setPreviewProgramId(firstProgram.id);
              }
            }}
            onProgramSelectionChanged={handleProgramSelectionChanged}
            onProgramPreviewSelect={setPreviewProgramId}
            icbtClaims={icbtClaims}
            feedbackDateFeatureEnabledForPatient={
              feedbackDateFeatureEnabledForPatient
            }
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            languages={languages}
          >
            <SelectIcbt.ProgramPreview
              title={programContent?.title}
              isLoading={programContentFetching}
              error={programContentError}
              onExpandPreview={
                isPreviewOnly ? () => setPreviewModalOpen(true) : undefined
              }
            >
              {programContent?.pages && (
                <ProgramPages
                  pages={programContent.pages}
                  enableAudio={enableAudio}
                  enableVideo={enableVideo}
                  renderQuestionnaire={(entry) => (
                    <Questionnaire
                      key={entry.sys.id}
                      questionnaireType="icbt"
                      clinicalId={entry.clinicalId}
                    />
                  )}
                />
              )}
            </SelectIcbt.ProgramPreview>
          </SelectIcbt>

          {effectivePreviewProgramId && (
            <PreviewProgramModal
              open={previewModalOpen}
              handleCloseProgramPreviewModal={() => setPreviewModalOpen(false)}
              programId={effectivePreviewProgramId}
              languageCode={selectedLanguage?.codeISO2}
            />
          )}
        </IcbtTranslationProvider>
      );
    }
    case 1:
      return (
        <SelectFeedbackDates
          programsToBeAssigned={programsToBeAssigned}
          isPreviewOnly={isPreviewOnly}
          isLoading={isLoading}
          setProgressStep={setProgressStep}
          treatments={treatments}
          icbtClaims={icbtClaims}
          programsToBeAssignedPairedWithTreatment={
            programsToBeAssignedPairedWithTreatment
          }
          handleSelectProgram={handleSelectProgram}
          handleOpenConfirm={handleOpenConfirm}
          isError={isError}
          possiblyErrors={possiblyErrors}
          patientUserId={patientUserId}
        />
      );
    default:
      return null;
  }
};
